/**
 * Базовый конверт моста web-сервис ↔ оболочка. Чистый TS без зависимостей.
 * Формат провода идентичен нативному `@app/webview-bridge` (packages/webview-bridge),
 * чтобы встроенный сервис (BridgeSdk поверх window.__nativeBridge) работал без правок.
 *
 * сервис → оболочка: { v, action, payload, id? }
 * оболочка → сервис: { v, event, payload, correlationId?, error? }
 *
 * На вебе «host» = веб-оболочка (super-app). В нативке этот тип называется NativeMessage —
 * поля и значения совпадают, отличается только название стороны.
 */
export const PROTOCOL_VERSION = 1 as const;
export type TProtocolVersion = typeof PROTOCOL_VERSION;

/** Сообщение из сервиса в оболочку. `id` присутствует, если сервис ждёт ответ (RPC). */
export interface IWebMessage<A extends string = string, P = unknown> {
  v: TProtocolVersion;
  action: A;
  payload: P;
  id?: string;
}

/** Ошибка ответа на RPC. */
export interface IBridgeError {
  code: string;
  message: string;
}

/** Сообщение из оболочки в сервис. `correlationId` эхо-ит `IWebMessage.id` для ответа на RPC. */
export interface IHostMessage<E extends string = string, P = unknown> {
  v: TProtocolVersion;
  event: E;
  payload: P;
  correlationId?: string;
  error?: IBridgeError;
}

/** Имя события-ответа на RPC. Сервис сопоставляет ответ по `correlationId`, не по имени. */
export const RPC_RESPONSE_EVENT = 'rpc.response' as const;
export type TRpcResponseEvent = typeof RPC_RESPONSE_EVENT;

export const BRIDGE_ERROR_CODES = {
  INVALID_MESSAGE: 'INVALID_MESSAGE',
  UNSUPPORTED_VERSION: 'UNSUPPORTED_VERSION',
  UNKNOWN_ACTION: 'UNKNOWN_ACTION',
  HANDLER_FAILED: 'HANDLER_FAILED'
} as const;
export type TBridgeErrorCode =
  (typeof BRIDGE_ERROR_CODES)[keyof typeof BRIDGE_ERROR_CODES];

/** Сконструировать событие оболочка → сервис (с опциональной корреляцией). */
export function hostEvent<E extends string, P>(
  event: E,
  payload: P,
  correlationId?: string
): IHostMessage<E, P> {
  const message: IHostMessage<E, P> = { v: PROTOCOL_VERSION, event, payload };
  if (correlationId !== undefined) message.correlationId = correlationId;
  return message;
}

/** Успешный ответ на RPC. */
export function rpcResult(
  correlationId: string,
  payload: unknown
): IHostMessage<TRpcResponseEvent> {
  return {
    v: PROTOCOL_VERSION,
    event: RPC_RESPONSE_EVENT,
    payload,
    correlationId
  };
}

/** Ответ-ошибка на RPC. */
export function rpcError(
  correlationId: string,
  error: IBridgeError
): IHostMessage<TRpcResponseEvent, null> {
  return {
    v: PROTOCOL_VERSION,
    event: RPC_RESPONSE_EVENT,
    payload: null,
    correlationId,
    error
  };
}
